import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Award,
  Bell,
  BookOpen,
  Calendar,
  CalendarCheck,
  QrCode,
  CalendarDays,
  CalendarPlus,
  CalendarClock,
  ClipboardCheck,
  ClipboardList,
  CreditCard,
  Download,
  Dumbbell,
  FileBarChart,
  FileUp,
  Globe,
  GraduationCap,
  History,
  Home,
  Layers,
  LayoutDashboard,
  LayoutGrid,
  ListChecks,
  MapPin,
  Medal,
  ScrollText,
  Settings,
  ToggleRight,
  ShieldCheck,
  ShieldPlus,
  UserCog,
  Users,
  UsersRound,
} from "lucide-react";
import { useAuth } from "../../lib/auth-context";
import { useClubFeatures, type ClubFeature } from "../../lib/club-features";
import type { ClubSwitch } from "../../lib/api-client";
import { can, isStaff, opensAdminWorkspace } from "../../lib/permissions";
import type { NavItem } from "./side-menu";

export interface NavConfig {
  home: string;
  items: NavItem[];
  /** Rarely-used pages, listed under a "Settings" heading after the main items. */
  settingsItems?: NavItem[];
  /** Mobile bottom bar; absent for roles that only get the hamburger. */
  bottomItems?: NavItem[];
  /** Shortcuts to another workspace, listed under a divider. */
  switchItems: NavItem[];
  roleLabel: string;
}

const ADMIN_BOOKINGS_PATH = "/admin/reservations";

const ADMIN_ITEMS: NavItem[] = [
  { labelKey: "nav.items.dashboard", path: "/admin", icon: LayoutGrid, permission: "reports.view" },
  { labelKey: "nav.items.students", path: "/admin/students", icon: UsersRound, permission: "members.view" },
  { labelKey: "nav.items.planning", path: "/admin/planning", icon: CalendarClock, permission: "planning.view" },
  { labelKey: "nav.items.attendance", path: "/admin/presences", icon: ClipboardCheck, permission: "attendance.correct" },
  // Dives, private lessons, runs. Always there for admins: it is where the first bookable offer gets created.
  { labelKey: "nav.items.bookings", path: ADMIN_BOOKINGS_PATH, icon: CalendarPlus, permission: "planning.manage" },
  { labelKey: "nav.items.levels", path: "/admin/classes", icon: ShieldCheck, permission: "planning.manage" },
  { labelKey: "nav.items.registrations", path: "/admin/inscriptions", icon: ClipboardList, permission: "registrations.review" },
  { labelKey: "nav.items.badges", path: "/admin/badges", icon: Award, permission: "badges.manage" },
  // Events lead to badges, so they sit next to the library the club awards from.
  { labelKey: "nav.items.events", path: "/admin/events", icon: CalendarDays, permission: "events.manage" },
  { labelKey: "nav.items.site", path: "/admin/site", icon: Globe, permission: "club.settings" },
  { labelKey: "nav.items.reports", path: "/admin/rapports", icon: FileBarChart, permission: "reports.view" },
];

const ADMIN_SETTINGS_ITEMS: NavItem[] = [
  { labelKey: "nav.items.features", path: "/admin/fonctionnalites", icon: ToggleRight, permission: "club.settings" },
  { labelKey: "nav.items.activities", path: "/admin/activites", icon: Dumbbell, permission: "club.settings" },
  { labelKey: "nav.items.payments", path: "/admin/paiements", icon: CreditCard, permission: "club.settings" },
  { labelKey: "nav.items.programs", path: "/admin/programs", icon: BookOpen, permission: "curriculum.manage" },
  { labelKey: "nav.items.imports", path: "/admin/imports", icon: FileUp, permission: "members.import" },
  { labelKey: "nav.items.auditLog", path: "/admin/audit-log", icon: ScrollText, permission: "audit.view" },
  { labelKey: "nav.items.locations", path: "/admin/lieux", icon: MapPin, permission: "planning.manage" },
  { labelKey: "nav.items.club", path: "/admin/club", icon: Settings, permission: "club.settings" },
  { labelKey: "nav.items.users", path: "/admin/users", icon: Users, permission: "members.view" },
  { labelKey: "nav.items.notifications", path: "/compte/notifications", icon: Bell },
  { labelKey: "nav.items.myProfile", path: "/compte/profil", icon: UserCog },
  { labelKey: "nav.items.myData", path: "/compte/donnees", icon: Download },
  { labelKey: "nav.items.myConsents", path: "/compte/consentements", icon: ShieldCheck },
];

const COACH_ITEMS: NavItem[] = [
  { labelKey: "nav.items.levels", path: "/coach/classes", icon: ShieldCheck, permission: "classes.manage_own" },
  { labelKey: "nav.items.attendance", path: "/coach/presences", icon: ClipboardCheck, permission: "attendance.take" },
  { labelKey: "nav.items.dashboard", shortLabelKey: "nav.items.tracking", path: "/coach/tableau-de-bord", icon: LayoutDashboard },
  { labelKey: "nav.items.programs", path: "/coach/programs", icon: BookOpen, permission: "curriculum.manage" },
  { labelKey: "nav.items.sessions", path: "/coach/sessions", icon: ListChecks, permission: "classes.manage_own" },
  { labelKey: "nav.items.calendar", path: "/coach/calendar", icon: Calendar },
  { labelKey: "nav.items.schedule", path: "/coach/horaires", icon: CalendarDays },
];

// Shown in the mobile bottom bar; everything else lives behind "Plus".
const COACH_BOTTOM_PATHS = ["/coach/classes", "/coach/presences", "/coach/tableau-de-bord", "/coach/sessions"];
const COACH_BOTTOM_ITEMS = COACH_ITEMS.filter((item) => COACH_BOTTOM_PATHS.includes(item.path));

const HISTORY_ITEM: NavItem = { labelKey: "nav.items.history", path: "/historique", icon: History };
// Badges travel with the person across clubs, so like the history the page sits outside /student.
const BADGES_ITEM: NavItem = { labelKey: "nav.items.myBadges", path: "/mes-badges", icon: Medal };

// Only for clubs with a class that takes bookings (a dive, a private lesson): see user.club.hasBookingClasses.
const BOOKINGS_PATH = "/student/reservations";

const STUDENT_ITEMS: NavItem[] = [
  { labelKey: "nav.items.home", path: "/student", icon: Home },
  { labelKey: "nav.items.card", path: "/student/carte", icon: QrCode },
  { labelKey: "nav.items.program", path: "/student/programme", icon: GraduationCap },
  { labelKey: "nav.items.attendance", path: "/student/presences", icon: CalendarCheck },
  { labelKey: "nav.items.flashcards", path: "/student/flashcards", icon: Layers },
  { labelKey: "nav.items.sessions", path: "/student/sessions", icon: ListChecks },
  { labelKey: "nav.items.schedule", path: "/student/horaires", icon: CalendarDays },
  { labelKey: "nav.items.bookings", path: BOOKINGS_PATH, icon: CalendarPlus },
  BADGES_ITEM,
  HISTORY_ITEM,
];

// The card, sessions and the timetable stay behind "Plus" on phones: too many tabs is too tight.
const STUDENT_BOTTOM_PATHS = ["/student", "/student/programme", "/student/presences", "/student/flashcards"];
const STUDENT_BOTTOM_ITEMS = STUDENT_ITEMS.filter((item) => STUDENT_BOTTOM_PATHS.includes(item.path));

const SWITCH_TO_COACH: NavItem = { labelKey: "nav.items.viewAsCoach", path: "/coach", icon: GraduationCap };
const SWITCH_TO_STUDENT: NavItem = { labelKey: "nav.items.viewAsStudent", path: "/student", icon: GraduationCap };
const SWITCH_TO_ADMIN: NavItem = { labelKey: "nav.items.backToAdmin", path: "/admin", icon: ShieldCheck };
const SWITCH_TO_SUPER_ADMIN: NavItem = { labelKey: "nav.items.superAdmin", path: "/super-admin", icon: ShieldPlus };

/** Screens only some activities need: hidden while no offered activity gives (and the club keeps) the feature. */
const FEATURE_BY_PATH: Record<string, ClubFeature> = {
  "/admin/programs": "program",
  "/coach/programs": "program",
  "/coach/sessions": "techniques",
  "/coach/calendar": "techniques",
  "/student/sessions": "techniques",
  "/student/flashcards": "techniques",
};

/** Screens that belong to a part of the app the club can switch off in its settings. */
const SWITCH_BY_PATH: Record<string, ClubSwitch> = {
  "/admin/inscriptions": "registration",
  "/admin/badges": "badges",
  "/mes-badges": "badges",
  "/admin/reservations": "booking",
  "/student/reservations": "booking",
  "/admin/events": "events",
  "/admin/site": "website",
};

/** The three workspaces a signed-in person can be in. */
type Workspace = "admin" | "coach" | "student";

/**
 * The workspace a URL belongs to, or null for the screens every workspace shares
 * (a session, a technique, the training history): those must never move the menu.
 */
function workspaceOfPath(pathname: string, isAdmin: boolean): Workspace | null {
  if (pathname.startsWith("/student")) return "student";
  if (pathname.startsWith("/coach")) return "coach";
  if (pathname.startsWith("/admin")) {
    // Programs live under /coach/programs for coaches, but keep the /admin/programs
    // URLs: a coach-only visitor there still gets the coach chrome.
    if (!isAdmin && pathname.startsWith("/admin/programs")) return "coach";
    return "admin";
  }
  return null;
}

/** Which navigation the signed-in person gets on the current screen. */
export function useNavConfig(): NavConfig {
  const { t } = useTranslation("common");
  const { user } = useAuth();
  const { pathname } = useLocation();
  const features = useClubFeatures();

  // The admin workspace belongs to whoever holds any of its permissions; every staff member has the coach one.
  const isAdmin = opensAdminWorkspace(user);
  // Not while impersonating: the super admin is looking through someone else's eyes.
  const isCoach = isStaff(user);
  // Staff who also train in the club get the student screens too (ROLE_STUDENT comes with the member flag).
  const isMember = user?.roles?.includes("ROLE_STUDENT") ?? false;
  const isSuperAdmin = !user?.impersonator && (user?.roles?.includes("ROLE_SUPER_ADMIN") ?? false);

  // Whoever has the admin workspace is staff, so may open the coach one too (not the other way round).
  const canOpen = (workspace: Workspace) =>
    workspace === "admin" ? isAdmin : workspace === "coach" ? isCoach : isMember;

  const pathWorkspace = workspaceOfPath(pathname, isAdmin);
  // The workspace the person belongs to by rank: where they stand when no URL says otherwise.
  const ownWorkspace: Workspace | null = isAdmin ? "admin" : isCoach ? "coach" : isMember ? "student" : null;

  // A shared screen keeps the workspace the person was already in, so opening a
  // session or a technique never swaps the menu under them. Only an explicit
  // "Voir comme…" link (or a workspace URL) changes it.
  const [sticky, setSticky] = useState<Workspace | null>(pathWorkspace);
  useEffect(() => {
    if (pathWorkspace) setSticky(pathWorkspace);
  }, [pathWorkspace]);

  const workspace: Workspace | null =
    (pathWorkspace && canOpen(pathWorkspace) ? pathWorkspace : null)
    ?? (sticky && canOpen(sticky) ? sticky : null)
    ?? ownWorkspace;

  // Memoized: AppShell re-renders on every route change, and a fresh object each
  // time would redraw the whole sidebar (and every NavList row) with it.
  return useMemo<NavConfig>(() => {
    // A diving centre sells "sorties", every other club "réservations": decided by the server.
    const wording = (item: NavItem): NavItem => (
      user?.club?.bookingLabel === "outings" && (item.path === BOOKINGS_PATH || item.path === ADMIN_BOOKINGS_PATH)
        ? { ...item, labelKey: "nav.items.outings" }
        : item
    );
    const visible = (items: NavItem[]) => items.map(wording).filter(
      (item) => (!SWITCH_BY_PATH[item.path] || features.enabled(SWITCH_BY_PATH[item.path]))
        && (!FEATURE_BY_PATH[item.path] || features.has(FEATURE_BY_PATH[item.path]))
        && (item.path !== BOOKINGS_PATH || user?.club?.hasBookingClasses === true)
        && (!item.permission || can(user, item.permission)),
    );

    if (workspace === "student") {
      return {
        home: "/student",
        items: visible(STUDENT_ITEMS),
        bottomItems: visible(STUDENT_BOTTOM_ITEMS),
        switchItems: [
          ...(isAdmin ? [SWITCH_TO_ADMIN] : []),
          ...(isCoach ? [SWITCH_TO_COACH] : []),
        ],
        roleLabel: t("nav.roles.student"),
      };
    }
    if (workspace === "coach") {
      return {
        home: "/coach/classes",
        items: visible(COACH_ITEMS),
        bottomItems: visible(COACH_BOTTOM_ITEMS),
        switchItems: [...(isAdmin ? [SWITCH_TO_ADMIN] : []), ...(isMember ? [SWITCH_TO_STUDENT] : [])],
        roleLabel: isAdmin ? t("nav.roles.coachAdminView") : t("nav.roles.coach"),
      };
    }
    if (workspace === "admin") {
      return {
        home: "/admin",
        items: visible(ADMIN_ITEMS),
        settingsItems: visible(ADMIN_SETTINGS_ITEMS),
        switchItems: [SWITCH_TO_COACH, ...(isMember ? [SWITCH_TO_STUDENT] : []), ...(isSuperAdmin ? [SWITCH_TO_SUPER_ADMIN] : [])],
        roleLabel: t("nav.roles.admin"),
      };
    }
    // No role in any active club: only the person's own history and badges are left to open.
    return { home: "/historique", items: [HISTORY_ITEM, ...(features.enabled("badges") ? [BADGES_ITEM] : [])], switchItems: [], roleLabel: t("nav.roles.member") };
  }, [t, features, user, workspace, isAdmin, isCoach, isMember, isSuperAdmin]);
}
